/**
 * Eigenbau: Projekt-Übersicht in der Form der Vorlage.
 *
 * Die Vorlage führt die Übersicht mit der Mono-Zeile (Projekt · seit · Vorlage),
 * dem Titel in 54, dann die Bereiche als Rubriken: Kurzbeschreibung, Zahlen,
 * Metarr, Stand mit dem Zeitband, Entscheidungen (Seiten), Team und
 * Lieferung. Was das Datenmodell nicht hergibt — Schaufenster-Bilder,
 * Systeme, Umgebungen, Termine, Risiken — bleibt weg: eine Übersicht
 * behauptet keine Zahlen, die niemand zählt.
 */

import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { schluessel } from "@/lib/abfragen";
import {
  useIch,
  useMeilensteinFortschritt,
  useMeilensteine,
  useNavigationszahlen,
  useProjekt,
  useProjektVerzeichnis,
  useZustaendeFuerProjekte,
} from "@/lib/haken";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Seitenkopf } from "@/components/entwurf/Seitenkopf";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Zeitband } from "@/components/entwurf/Zeitband";
import { MetarrKarte } from "@/components/entwurf/MetarrKarte";
import { verweisKlassen } from "@/components/entwurf/Verweis";
import { leererFilter } from "@/lib/filter";
import { useAufgabenFuerProjekte } from "@/lib/haken";

const MONATSNAME = [
  "Jan",
  "Feb",
  "Mär",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
] as const;

/** 0–100-Prozentposition eines Datums zwischen zwei Grenzen. */
function position(wert: Date, von: Date, bis: Date): string {
  const gesamt = bis.getTime() - von.getTime();
  if (gesamt <= 0) return "0%";
  const anteil = Math.min(
    Math.max((wert.getTime() - von.getTime()) / gesamt, 0),
    1,
  );
  return `${(anteil * 100).toFixed(1)}%`;
}

/**
 * Breite einer Spanne in Prozent des Fensters — nicht die rechte Kante:
 * Sobald das Fenster vor der frühesten Anlage beginnt, liegt `links` über
 * 0 %, und eine als „Breite" durchgereichte Kantenposition liefe über das
 * Band hinaus.
 */
function spanne(von: Date, bis: Date, fensterVon: Date, fensterBis: Date): string {
  const gesamt = fensterBis.getTime() - fensterVon.getTime();
  if (gesamt <= 0) return "0%";
  const anteil = (wert: Date) =>
    Math.min(Math.max((wert.getTime() - fensterVon.getTime()) / gesamt, 0), 1);
  return `${(Math.max(anteil(bis) - anteil(von), 0) * 100).toFixed(1)}%`;
}

function tageBis(wert: string | Date | null | undefined): number | null {
  if (!wert) return null;
  return Math.max(
    0,
    Math.ceil(
      (new Date(wert).getTime() - Date.now()) / (24 * 60 * 60 * 1_000),
    ),
  );
}

/** Füllung einer Bahn — lädt den echten Fortschritt des Meilensteins. */
function BahnFuellung({ id }: { id: string }) {
  const f = useMeilensteinFortschritt(id);
  const wert =
    f.data && f.data.gesamt > 0
      ? Math.round((f.data.erledigt / f.data.gesamt) * 100)
      : 0;
  return (
    <span className="block h-full bg-tinte" style={{ width: `${wert}%` }} />
  );
}

export function UebersichtReiter({ projektId }: { projektId: string }) {
  const projekt = useProjekt(projektId);
  const ich = useIch();
  const verzeichnis = useProjektVerzeichnis(projektId);
  const zahlen = useNavigationszahlen(projektId);
  const meilensteine = useMeilensteine(projektId);
  const zustaende = useZustaendeFuerProjekte([projektId]);

  const aufgaben = useAufgabenFuerProjekte([projektId], leererFilter());
  const seiten = useQuery({
    queryKey: schluessel.seitenBaum(projektId),
    queryFn: () => api.seiten.baum.query({ projektId }),
    enabled: Boolean(projektId),
  });

  const zustandsGruppen = useMemo(() => {
    const m = new Map<string, string>();
    for (const z of zustaende.data ?? []) m.set(z.id, z.gruppe);
    return m;
  }, [zustaende.data]);

  const gitlab = useQuery({
    queryKey: ["cubic", "gitlab", "uebersicht", projektId],
    queryFn: async () => {
      const [mrs, pipelines, commits, auslieferungen] = await Promise.all([
        api.gitlab.mergeRequests
          .query({ projektId, grenze: 50 })
          .catch(() => ({ eintraege: [], naechsterCursor: null })),
        api.gitlab.pipelines
          .query({ projektId, grenze: 50 })
          .catch(() => ({ eintraege: [], naechsterCursor: null })),
        api.gitlab.commits
          .query({ projektId, grenze: 50 })
          .catch(() => ({ eintraege: [], naechsterCursor: null })),
        api.gitlab.auslieferungen
          .query({ projektId, grenze: 50 })
          .catch(() => ({ eintraege: [], naechsterCursor: null })),
      ]);
      return { mrs, pipelines, commits, auslieferungen };
    },
    enabled: Boolean(projektId),
  });

  const offeneAufgaben = useMemo(
    () =>
      (aufgaben.data ?? []).filter((a) => {
        const gruppe = a.zustandId
          ? zustandsGruppen.get(a.zustandId)
          : undefined;
        return gruppe !== "erledigt" && gruppe !== "verworfen";
      }).length,
    [aufgaben.data, zustandsGruppen],
  );

  const erledigteAufgaben = useMemo(
    () =>
      (aufgaben.data ?? []).filter((a) => {
        const gruppe = a.zustandId
          ? zustandsGruppen.get(a.zustandId)
          : undefined;
        return gruppe === "erledigt";
      }).length,
    [aufgaben.data, zustandsGruppen],
  );

  const personen = verzeichnis.data?.personen ?? [];
  const ohneArchiv = (meilensteine.data ?? []).filter((m) => !m.archiviertAm);

  /*
   * Zeitband-Fenster: früheste Anlage bis späteste Frist. Die linke Kante
   * reicht mindestens bis zum Ersten des Vormonats zurück — erst mit dem
   * zurückliegenden Monat im Band steht die Heute-Linie frei, und der
   * gefüllte Teil der Bahnen lässt sich als „so viel ist geschafft" lesen.
   */
  const fenster = useMemo(() => {
    if (ohneArchiv.length === 0) return null;
    const zeiten = ohneArchiv.flatMap((m) => {
      const von = m.angelegtAm ? new Date(m.angelegtAm).getTime() : null;
      const bis = m.faelligAm ? new Date(m.faelligAm).getTime() : null;
      return [von, bis].filter((t): t is number => t !== null);
    });
    if (zeiten.length === 0) return null;
    const jetzt = new Date();
    const vormonat = new Date(jetzt.getFullYear(), jetzt.getMonth() - 1, 1);
    return {
      von: new Date(Math.min(...zeiten, vormonat.getTime())),
      bis: new Date(Math.max(...zeiten)),
    };
  }, [ohneArchiv]);

  const achse = useMemo(() => {
    if (!fenster) return [];
    const monate: string[] = [];
    const start = new Date(fenster.von.getFullYear(), fenster.von.getMonth(), 1);
    const ende = new Date(fenster.bis.getFullYear(), fenster.bis.getMonth() + 1, 0);
    for (const d = new Date(start); d <= ende; d.setMonth(d.getMonth() + 1)) {
      monate.push(MONATSNAME[d.getMonth()] ?? "");
    }
    return monate;
  }, [fenster]);

  const bahnen = useMemo(() => {
    if (!fenster) return [];
    return ohneArchiv.map((m) => {
      const von = m.angelegtAm ? new Date(m.angelegtAm) : new Date(m.faelligAm);
      const bis = new Date(m.faelligAm);
      return {
        name: m.name,
        links: position(von, fenster.von, fenster.bis),
        breite: spanne(von, bis, fenster.von, fenster.bis),
        fuellung: "0%",
        grund: "var(--wisch)",
        fuell: "var(--tinte)",
        fuellungInhalt: <BahnFuellung id={m.id} />,
      };
    });
  }, [ohneArchiv, fenster]);

  const naechster = useMemo(
    () =>
      ohneArchiv.sort(
        (a, b) =>
          new Date(a.faelligAm).getTime() - new Date(b.faelligAm).getTime(),
      )[0] ?? null,
    [ohneArchiv],
  );

  const tage = naechster ? tageBis(naechster.faelligAm) : null;

  const gitlabZahlen = gitlab.data
    ? {
        mrs: `${gitlab.data.mrs.eintraege.length}${gitlab.data.mrs.naechsterCursor ? "+" : ""}`,
        pipelines: `${gitlab.data.pipelines.eintraege.length}${gitlab.data.pipelines.naechsterCursor ? "+" : ""}`,
        commits: `${gitlab.data.commits.eintraege.length}${gitlab.data.commits.naechsterCursor ? "+" : ""}`,
        auslieferungen: `${gitlab.data.auslieferungen.eintraege.length}${gitlab.data.auslieferungen.naechsterCursor ? "+" : ""}`,
      }
    : null;

  if (projekt.isLoading) {
    return <Mikrolabel grad={12}>Lade Projekt …</Mikrolabel>;
  }

  const p = projekt.data;
  if (!p) return null;

  const seit = p.angelegtAm
    ? new Date(p.angelegtAm).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

  const entscheidungen = (seiten.data ?? []).slice(0, 7);

  /*
   * Keine eigene Polsterung mehr: Die Hülle liefert `lg:px-9 lg:pt-14
   * lg:pb-24`. Das `px-9 pb-24` hier kam obendrauf und setzte diese Seite
   * 36px weiter rechts und den Titel 56px tiefer als auf jeder anderen Seite.
   */
  return (
    <div>
      <Seitenkopf kontext={`Projekt ${p.kuerzel} · seit ${seit}`} grad={54}>
        {p.name}
      </Seitenkopf>
      <div className="max-w-[1160px]">

        {p.beschreibung ? (
          <div className="mt-[60px]">
            <Rubrik aktion={<span className="font-mono text-[11.5px] text-tinte4">Bearbeiten</span>}>
              Kurzbeschreibung
            </Rubrik>
            <p className="mt-[26px] max-w-[66ch] text-[18px] font-light leading-[1.7] text-tinte2 text-pretty">
              {p.beschreibung}
            </p>
          </div>
        ) : null}

        <div className="mt-[60px]">
          <Rubrik anzahl="seit dem ersten Commit">Zahlen</Rubrik>
          <div className="mt-[30px] grid grid-cols-[repeat(3,1fr)] gap-x-12 gap-y-[38px]">
            {[
              { label: "offene Aufgaben", wert: offeneAufgaben },
              { label: "erledigte Aufgaben", wert: erledigteAufgaben },
              { label: "offene Klärungen", wert: zahlen.data?.klaerungen ?? 0 },
              { label: "Meilensteine", wert: ohneArchiv.length },
              { label: "Personen", wert: personen.length },
              { label: "Merge Requests", wert: gitlabZahlen?.mrs ?? 0 },
            ].map((f) => (
              <div key={f.label}>
                <Mikrolabel grad={11.5} ton="leise">
                  {f.label}
                </Mikrolabel>
                <div className="mt-2 text-[34px] font-extralight leading-none tracking-[-0.03em] tabular-nums">
                  {f.wert}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-[60px]">
          <Rubrik aktion={
            <Link to="/metarr" className={verweisKlassen()}>
              Bericht öffnen
            </Link>
          }>
            Metarr
          </Rubrik>
          <MetarrKarte className="mt-[26px]" />
        </div>

        {naechster && fenster && bahnen.length > 0 ? (
          <div className="mt-[60px]">
            <Rubrik
              anzahl={`${ohneArchiv.length} ${ohneArchiv.length === 1 ? "Meilenstein" : "Meilensteine"}`}
              aktion={
                <Link
                  to="/projekte/$projektId/meilensteine/$meilensteinId"
                  params={{ projektId, meilensteinId: naechster.id }}
                  className={verweisKlassen()}
                >
                  {naechster.name} öffnen
                </Link>
              }
            >
              Stand
            </Rubrik>
            <div className="mt-[30px] flex items-start gap-14 flex-wrap">
              <div className="flex-none">
                <Mikrolabel grad={12} ton="leise">
                  Tage bis {naechster.name}
                </Mikrolabel>
                <div className="mt-1 text-[62px] font-extralight leading-[0.9] tracking-[-0.045em] tabular-nums text-akzent">
                  {tage != null ? tage : "—"}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <Zeitband
                  achse={achse}
                  bahnen={bahnen}
                  heute={position(new Date(), fenster.von, fenster.bis)}
                />
              </div>
            </div>
          </div>
        ) : null}

        {entscheidungen.length > 0 ? (
          <div className="mt-[60px]">
            <Rubrik anzahl="jede steht als Seite">Entscheidungen</Rubrik>
            {entscheidungen.map((s) => (
              <Link
                key={s.id}
                to="/projekte/$projektId/seiten/$seiteId"
                params={{ projektId, seiteId: s.id }}
                className="flex h-14 cursor-pointer items-center gap-5 border-b border-linie hover:bg-wisch"
              >
                <span className="min-w-0 flex-1 truncate text-[15px] text-tinte">
                  {s.titel}
                </span>
                <span aria-hidden className="w-3 flex-none text-right font-mono text-[12px] text-ring">
                  ›
                </span>
              </Link>
            ))}
          </div>
        ) : null}

        {personen.length > 0 ? (
          <div className="mt-[60px]">
            <Rubrik anzahl="Last aus offenen Aufgaben">Team</Rubrik>
            {personen.map((person) => (
              <div
                key={person.id}
                className="flex h-[62px] items-center gap-[18px] border-b border-linie"
              >
                <span className="flex h-[30px] w-[30px] flex-none items-center justify-center shadow-[inset_0_0_0_1px_var(--linie3)] font-mono text-[10.5px] text-tinte3">
                  {person.name
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((t) => t[0]!.toUpperCase())
                    .join("")}
                </span>
                <span className="w-[180px] flex-none text-[15px] text-tinte">
                  {person.name}
                  {person.id === ich.data?.personId ? " · Du" : ""}
                </span>
                <span className="min-w-0 flex-1 text-[13.5px] font-light text-tinte3">
                  {person.rolle}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        <div className="mt-[60px]">
          <Rubrik
            anzahl={
              p.gitlabProjekt
                ? `GitLab · ${p.gitlabProjekt}`
                : "noch nicht verbunden"
            }
            aktion={
              <Link
                to="/projekte/$id"
                params={{ id: projektId }}
                className={verweisKlassen()}
              >
                Lieferung öffnen
              </Link>
            }
          >
            Lieferung
          </Rubrik>
          <div className="mt-[30px] grid grid-cols-[repeat(4,1fr)] gap-x-12 gap-y-[38px]">
            {[
              { label: "Merge Requests", wert: gitlabZahlen?.mrs ?? 0 },
              { label: "Pipelines", wert: gitlabZahlen?.pipelines ?? 0 },
              { label: "Commits", wert: gitlabZahlen?.commits ?? 0 },
              { label: "Auslieferungen", wert: gitlabZahlen?.auslieferungen ?? 0 },
            ].map((f) => (
              <div key={f.label}>
                <Mikrolabel grad={11.5} ton="leise">
                  {f.label}
                </Mikrolabel>
                <div className="mt-2 text-[34px] font-extralight leading-none tracking-[-0.03em] tabular-nums">
                  {f.wert}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
